import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Ticket, PlugZap, CheckCircle2, XCircle, Loader2, PauseCircle, Circle, Radio, Ban,
  ArrowLeft, ShieldCheck, ShieldAlert, Check, Clock, AlertTriangle, MessageSquare,
  GitBranch, UserPlus, SkipForward, Link2,
} from 'lucide-react';
import { useRunEvents } from '../../hooks/useRunEvents';
import pipelineService, {
  JiraProposal, JiraConnectionStatus, JiraDecision, PipelineRun, GuardrailReport, StageResult,
} from '../../services/pipelineService';
import { Agent } from '../../services/agentsService';
import { PageHeader, Spinner, StatusPill, Button } from '../../components/ui';
import EventTimeline from '../../components/ui/EventTimeline';
import AgentChatPanel from '../../components/AgentChatPanel';
import '../../styles/theme.css';

/**
 * IT / Jira agent workspace: connect to Jira Cloud, pull tickets (with late/spillover focus),
 * watch the shared pipeline run live (fetch → triage → guardrails → review → apply → finalize),
 * then review each proposed action and apply the approved ones back to Jira. Runs on the same
 * shared engine (orchestrator + live SSE bus + human gate) as the Doc and Marketing agents.
 */

const PHASES: { key: string; label: string }[] = [
  { key: 'fetch', label: 'Fetch' },
  { key: 'triage', label: 'Triage' },
  { key: 'guardrail_check', label: 'Guardrails' },
  { key: 'human_review', label: 'Your review' },
  { key: 'apply', label: 'Apply' },
  { key: 'finalize', label: 'Done' },
];

function iconFor(status: string, size = 20) {
  switch (status) {
    case 'succeeded': return <CheckCircle2 size={size} color="var(--mx-success)" />;
    case 'running': return <Loader2 size={size} color="var(--mx-accent)" className="mx-spin" />;
    case 'paused': return <PauseCircle size={size} color="var(--mx-warning)" />;
    case 'failed': return <XCircle size={size} color="var(--mx-danger)" />;
    default: return <Circle size={size} color="var(--mx-border-strong)" />;
  }
}

const Stepper: React.FC<{ stages: StageResult[] }> = ({ stages }) => {
  const statusOf = (name: string) => stages.find((s) => s.stage === name)?.status || 'pending';
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
      {PHASES.map((p, i) => {
        const status = statusOf(p.key);
        const done = status === 'succeeded';
        return (
          <React.Fragment key={p.key}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto', width: 92 }}>
              {iconFor(status)}
              <div style={{ fontSize: 11.5, fontWeight: 600, marginTop: 6, textAlign: 'center',
                color: status === 'pending' ? 'var(--mx-text-subtle)' : 'var(--mx-text)' }}>{p.label}</div>
            </div>
            {i < PHASES.length - 1 && (
              <div style={{ flex: 1, height: 2, marginTop: 10, borderRadius: 2,
                background: done ? 'var(--mx-success)' : 'var(--mx-border)' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ------------------------------------------------------------------ connection panel
const ConnectionPanel: React.FC<{ onReady: (ok: boolean) => void }> = ({ onReady }) => {
  const [conn, setConn] = useState<JiraConnectionStatus | null>(null);
  const [site, setSite] = useState('');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState('');
  const [test, setTest] = useState<{ ok: boolean; account?: any; error?: string } | null>(null);

  const load = useCallback(() => {
    pipelineService.getJiraConnection().then((c) => {
      setConn(c); setSite(c.site || ''); setEmail(c.email || ''); onReady(c.configured);
    }).catch(() => onReady(false));
  }, [onReady]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy('save'); setTest(null);
    try {
      const c = await pipelineService.setJiraConnection({ site, email, token: token || undefined });
      setConn(c); setToken(''); onReady(c.configured);
    } catch (e: any) {
      setTest({ ok: false, error: e?.response?.data?.detail || 'Could not save connection.' });
    } finally { setBusy(''); }
  };
  const runTest = async () => {
    setBusy('test');
    try { setTest(await pipelineService.testJiraConnection()); }
    catch (e: any) { setTest({ ok: false, error: e?.response?.data?.detail || 'Test failed.' }); }
    finally { setBusy(''); }
  };

  const field = (label: string, node: React.ReactNode) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ fontSize: 12.5, fontWeight: 600 }}>{label}</span>{node}
    </label>
  );

  return (
    <div className="mx-card">
      <div className="mx-card-head">
        <h3 className="mx-card-title" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <Link2 size={15} /> Jira connection</h3>
        {conn?.configured && <span className="mx-chip" style={{ color: 'var(--mx-success)' }}>
          <CheckCircle2 size={12} /> connected</span>}
      </div>
      <div className="mx-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {field('Site URL', <input className="mx-input" placeholder="https://your-team.atlassian.net"
          value={site} onChange={(e) => setSite(e.target.value)} />)}
        {field('Account email', <input className="mx-input" placeholder="you@example.com"
          value={email} onChange={(e) => setEmail(e.target.value)} />)}
        {field(conn?.token_configured ? `API token (stored ${conn.token_preview} — leave blank to keep)` : 'API token',
          <input className="mx-input" type="password" placeholder="Atlassian API token"
            value={token} onChange={(e) => setToken(e.target.value)} />)}
        <div className="mx-subtle" style={{ fontSize: 11.5 }}>
          The token is stored encrypted and never shown again. Create one at id.atlassian.com → API tokens.</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="primary" size="sm" disabled={!!busy} onClick={save}>
            {busy === 'save' ? 'Saving…' : 'Save connection'}</Button>
          <Button icon={PlugZap} variant="secondary" size="sm" disabled={!!busy || !conn?.configured} onClick={runTest}>
            {busy === 'test' ? 'Testing…' : 'Test connection'}</Button>
        </div>
        {test && (
          <div style={{ fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 7,
            color: test.ok ? 'var(--mx-success)' : 'var(--mx-danger)' }}>
            {test.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
            {test.ok
              ? `Connected as ${test.account?.display_name || test.account?.email} · ${test.account?.site}`
              : test.error}
          </div>
        )}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ start form
const StartForm: React.FC<{ agent: Agent; enabled: boolean; onStarted: (id: string) => void }> = ({ agent, enabled, onStarted }) => {
  const p = agent.params || {};
  const [projectKey, setProjectKey] = useState<string>(p.project_key || 'KAN');
  const [spillover, setSpillover] = useState<string>(String(p.spillover_days ?? 7));
  const [maxTickets, setMaxTickets] = useState<string>(String(p.max_tickets ?? 25));
  const [jql, setJql] = useState<string>(p.jql || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const { run } = await pipelineService.startJiraRun({
        project_key: projectKey, jql: jql || undefined,
        spillover_days: Number(spillover) || undefined, max_tickets: Number(maxTickets) || undefined,
      });
      onStarted(run.id);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Could not start the run.');
    } finally { setBusy(false); }
  };
  const field = (label: string, node: React.ReactNode) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ fontSize: 12.5, fontWeight: 600 }}>{label}</span>{node}
    </label>
  );

  return (
    <div className="mx-card">
      <div className="mx-card-head">
        <h3 className="mx-card-title" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <Ticket size={15} /> Triage a project</h3>
      </div>
      <div className="mx-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="mx-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {field('Project key', <input className="mx-input" value={projectKey}
            onChange={(e) => setProjectKey(e.target.value.toUpperCase())} />)}
          {field('Spillover days', <input className="mx-input" type="number" min={0} value={spillover}
            onChange={(e) => setSpillover(e.target.value)} />)}
          {field('Max tickets', <input className="mx-input" type="number" min={1} value={maxTickets}
            onChange={(e) => setMaxTickets(e.target.value)} />)}
        </div>
        {field('JQL override (optional)', <input className="mx-input" placeholder='e.g. assignee = currentUser() AND statusCategory != Done'
          value={jql} onChange={(e) => setJql(e.target.value)} />)}
        <div className="mx-subtle" style={{ fontSize: 11.5 }}>
          Pulls open tickets and flags any that are past due or aged beyond the spillover window.</div>
        {err && <div style={{ color: 'var(--mx-danger)', fontSize: 12.5 }}>{err}</div>}
        <div>
          <Button icon={Ticket} variant="primary" disabled={busy || !enabled} onClick={submit}>
            {busy ? 'Starting…' : 'Triage tickets'}</Button>
          {!enabled && <span className="mx-subtle" style={{ fontSize: 12, marginLeft: 10 }}>Connect to Jira first.</span>}
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ guardrail report
const GuardrailPanel: React.FC<{ report?: GuardrailReport }> = ({ report }) => {
  if (!report || !report.checks?.length) return null;
  const ok = report.passed;
  return (
    <div className="mx-card" style={{ borderColor: ok ? 'var(--mx-success)' : 'var(--mx-warning)' }}>
      <div className="mx-card-head" style={{ background: ok ? 'var(--mx-success-weak)' : 'var(--mx-warning-weak)' }}>
        <h3 className="mx-card-title" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          {ok ? <ShieldCheck size={16} color="var(--mx-success)" /> : <ShieldAlert size={16} color="var(--mx-warning)" />}
          Guardrail check</h3>
        <span className="mx-subtle" style={{ fontSize: 12 }}>{ok ? 'All clear' : `${report.violations} to review`}</span>
      </div>
      <div className="mx-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {report.checks.map((c) => (
          <div key={c.key} style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
            {c.passed ? <Check size={15} style={{ color: 'var(--mx-success)', flex: 'none', marginTop: 1 }} />
              : <ShieldAlert size={15} style={{ color: c.severity === 'violation' ? 'var(--mx-danger)' : 'var(--mx-warning)', flex: 'none', marginTop: 1 }} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
              <div className="mx-muted" style={{ fontSize: 12.5 }}>{c.detail}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ACTION_META: Record<string, { icon: any; label: string }> = {
  comment: { icon: MessageSquare, label: 'Comment' },
  transition: { icon: GitBranch, label: 'Transition' },
  assign: { icon: UserPlus, label: 'Assign' },
  none: { icon: SkipForward, label: 'No action' },
};

// ------------------------------------------------------------------ per-ticket review row
const TicketRow: React.FC<{
  p: JiraProposal;
  decision: JiraDecision;
  editable: boolean;
  onChange: (d: JiraDecision) => void;
}> = ({ p, decision, editable, onChange }) => {
  const meta = ACTION_META[decision.action] || ACTION_META.comment;
  const Icon = meta.icon;
  return (
    <div className="mx-card mx-card-pad" style={{ border: '1px solid var(--mx-border)',
      borderLeft: `3px solid ${p.late ? 'var(--mx-warning)' : 'var(--mx-border)'}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>{p.issue_key}</span>
        <span style={{ fontSize: 13, flex: 1, minWidth: 120 }}>{p.triage_summary || p.summary}</span>
        {p.status && <span className="mx-chip">{p.status}</span>}
        {p.late && <span className="mx-chip" style={{ color: 'var(--mx-warning)' }}>
          <AlertTriangle size={11} /> {p.overdue ? 'overdue' : 'spillover'}</span>}
        {(p.proposed_priority || p.priority) && <span className="mx-chip">{p.proposed_priority || p.priority}</span>}
      </div>
      {p.late_reason && (
        <div className="mx-subtle" style={{ fontSize: 11.5, marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
          <Clock size={11} /> {p.late_reason}</div>
      )}
      {p.reason && <div className="mx-muted" style={{ fontSize: 12, marginTop: 4 }}>Why: {p.reason}</div>}
      {p.blocked && (
        <div style={{ fontSize: 12, marginTop: 6, color: 'var(--mx-danger)', display: 'flex', alignItems: 'center', gap: 5 }}>
          <ShieldAlert size={12} /> {p.blocked_reason}</div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
          <Icon size={13} /> {meta.label}</span>
        {editable && (
          <select className="mx-input" style={{ width: 'auto', padding: '3px 8px', fontSize: 12 }}
            value={decision.action} onChange={(e) => onChange({ ...decision, action: e.target.value })}>
            <option value="comment">Comment</option>
            <option value="transition">Transition</option>
            <option value="assign">Assign</option>
            <option value="none">Skip</option>
          </select>
        )}
      </div>

      {decision.action === 'comment' && (
        <textarea className="mx-input" style={{ width: '100%', minHeight: 64, marginTop: 8, fontSize: 12.5, lineHeight: 1.45 }}
          value={decision.comment || ''} disabled={!editable}
          onChange={(e) => onChange({ ...decision, comment: e.target.value })} />
      )}
      {decision.action === 'transition' && (
        <input className="mx-input" style={{ marginTop: 8, fontSize: 12.5 }} placeholder="Target status (e.g. In Progress)"
          value={decision.transition || ''} disabled={!editable}
          onChange={(e) => onChange({ ...decision, transition: e.target.value })} />
      )}
      {decision.action === 'assign' && (
        <input className="mx-input" style={{ marginTop: 8, fontSize: 12.5 }} placeholder="Assignee accountId"
          value={decision.assignee_account_id || ''} disabled={!editable}
          onChange={(e) => onChange({ ...decision, assignee_account_id: e.target.value })} />
      )}

      {editable && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 8, fontSize: 12.5, cursor: 'pointer' }}>
          <input type="checkbox" checked={decision.approved && decision.action !== 'none'}
            disabled={decision.action === 'none'}
            onChange={(e) => onChange({ ...decision, approved: e.target.checked })} />
          Approve this action
        </label>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ live run view
const RunView: React.FC<{ runId: string; onBack: () => void }> = ({ runId, onBack }) => {
  const { run, feed, connected, reload } = useRunEvents(runId);
  const [decisions, setDecisions] = useState<Record<string, JiraDecision>>({});
  const [busy, setBusy] = useState('');

  const gate = run?.hitl_gate?.gate;
  const atReview = gate === 'human_review' && run?.status === 'paused';
  const proposals: JiraProposal[] = run?.hitl_gate?.payload?.proposals || run?.result?.proposals || [];
  const report: GuardrailReport | undefined =
    run?.hitl_gate?.payload?.guardrail_report || run?.result?.guardrail_report;
  const counts = run?.result?.counts || run?.hitl_gate?.payload?.counts || {};

  // Seed the per-ticket decisions from the proposals when the gate opens.
  useEffect(() => {
    if (!atReview || !proposals.length) return;
    setDecisions((prev) => {
      if (Object.keys(prev).length) return prev;
      const seed: Record<string, JiraDecision> = {};
      proposals.forEach((p) => {
        const action = p.action || 'comment';
        seed[p.issue_key] = {
          issue_key: p.issue_key,
          action,
          approved: !p.blocked && action !== 'none',   // block a violating action by default
          comment: p.comment || '',
          transition: p.transition_target || '',
          assignee_account_id: p.assignee_account_id || '',
        };
      });
      return seed;
    });
  }, [atReview, proposals]);

  const activeRun = run && ['running', 'paused', 'cancelling'].includes(run.status);
  const terminal = run && ['succeeded', 'failed', 'cancelled'].includes(run.status);
  const applyResults = run?.result?.apply_results || [];
  const applyCounts = run?.result?.apply_counts;

  const apply = async () => {
    setBusy('apply');
    try {
      await pipelineService.resumeJiraRun(runId, Object.values(decisions));
      await reload();
    } finally { setBusy(''); }
  };
  const cancel = async () => {
    setBusy('cancel');
    try { await pipelineService.cancelRun(runId); await reload(); } finally { setBusy(''); }
  };

  if (!run) return <div className="mx-card mx-card-pad"><Spinner label="Loading…" /></div>;

  const approvedCount = Object.values(decisions).filter((d) => d.approved && d.action !== 'none').length;

  return (
    <div>
      <Button icon={ArrowLeft} variant="ghost" size="sm" style={{ marginBottom: 12, paddingLeft: 4 }}
        onClick={onBack}>New triage</Button>
      <PageHeader title={`${run.result?.project_key || 'Jira'} triage`}
        subtitle={run.status === 'succeeded' ? 'Actions applied to Jira'
          : atReview ? 'Review and approve the proposed actions' : 'Pulling and triaging tickets…'}
        actions={<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <StatusPill status={run.status} pulse />
          {connected && <span className="mx-chip" style={{ color: 'var(--mx-success)' }}>
            <Radio size={12} className="mx-pulsing" /> live</span>}
          {activeRun && <Button icon={Ban} variant="danger" size="sm" disabled={!!busy} onClick={cancel}>Cancel</Button>}
        </div>} />

      <div className="mx-card mx-card-pad" style={{ marginBottom: 16 }}>
        <Stepper stages={run.stages} />
        <div className="mx-subtle" style={{ fontSize: 12, marginTop: 10, display: 'flex', gap: 14 }}>
          <span><b>{counts.total ?? proposals.length}</b> tickets</span>
          <span style={{ color: 'var(--mx-warning)' }}><b>{counts.late ?? 0}</b> late / spillover</span>
          {atReview && <span><b>{approvedCount}</b> approved to apply</span>}
        </div>
      </div>

      <div className="mx-grid" style={{ gridTemplateColumns: '1.6fr 1fr', alignItems: 'start', gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {proposals.length === 0 && terminal && (
            <div className="mx-card mx-card-pad mx-muted">No open tickets matched.</div>
          )}
          {proposals.map((p) => (
            <TicketRow key={p.issue_key} p={p}
              decision={decisions[p.issue_key] || { issue_key: p.issue_key, action: p.action || 'comment', approved: false, comment: p.comment }}
              editable={!!atReview}
              onChange={(d) => setDecisions((prev) => ({ ...prev, [p.issue_key]: d }))} />
          ))}
          {atReview && (
            <div className="mx-card mx-card-pad" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Button icon={Check} variant="primary" disabled={!!busy} onClick={apply}>
                {busy === 'apply' ? 'Applying…' : `Apply ${approvedCount} approved action(s)`}</Button>
              <span className="mx-subtle" style={{ fontSize: 12 }}>Unchecked tickets are skipped — nothing else is touched.</span>
            </div>
          )}
          {applyResults.length > 0 && (
            <div className="mx-card">
              <div className="mx-card-head"><h3 className="mx-card-title">Applied to Jira</h3>
                {applyCounts && <span className="mx-subtle" style={{ fontSize: 12 }}>
                  {applyCounts.applied} applied · {applyCounts.skipped} skipped · {applyCounts.errored} error(s)</span>}
              </div>
              <div className="mx-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {applyResults.map((r, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                    {r.status === 'applied' ? <CheckCircle2 size={14} color="var(--mx-success)" />
                      : r.status === 'error' ? <XCircle size={14} color="var(--mx-danger)" />
                      : <SkipForward size={14} color="var(--mx-text-subtle)" />}
                    <b>{r.issue_key}</b> · {r.action} · {r.status}{r.detail ? ` (${r.detail})` : ''}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <GuardrailPanel report={report} />
          <AgentChatPanel agentKey="jira" runId={runId} />
          <div className="mx-card">
            <div className="mx-card-head"><h3 className="mx-card-title">Progress</h3></div>
            <div className="mx-card-pad" style={{ maxHeight: 320, overflowY: 'auto' }}>
              <EventTimeline feed={feed} live={!!connected} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ entry
const JiraWorkspace: React.FC<{ agent: Agent }> = ({ agent }) => {
  const [runId, setRunId] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [recent, setRecent] = useState<PipelineRun[]>([]);

  const loadRecent = useCallback(() => {
    pipelineService.listRuns(1, 5, undefined, 'jira').then((r) => setRecent(r.runs)).catch(() => setRecent([]));
  }, []);
  useEffect(() => { if (!runId) loadRecent(); }, [runId, loadRecent]);
  const onReady = useMemo(() => (ok: boolean) => setReady(ok), []);

  if (runId) return <RunView runId={runId} onBack={() => setRunId(null)} />;

  return (
    <div>
      <PageHeader title={agent.name} subtitle={agent.persona || agent.description} />
      <div className="mx-grid" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <ConnectionPanel onReady={onReady} />
          <StartForm agent={agent} enabled={ready} onStarted={(id) => setRunId(id)} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <AgentChatPanel agentKey="jira" />
          {recent.length > 0 && (
          <div className="mx-card">
            <div className="mx-card-head"><h3 className="mx-card-title">Recent triage runs</h3></div>
            <div className="mx-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recent.map((r) => (
                <button key={r.id} className="mx-card mx-card-pad" style={{ textAlign: 'left', cursor: 'pointer', border: '1px solid var(--mx-border)' }}
                  onClick={() => setRunId(r.id)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>
                      {r.brief?.project_key || r.result?.project_key || 'Jira'} triage</span>
                    <StatusPill status={r.status} />
                  </div>
                  <div className="mx-subtle" style={{ fontSize: 11.5, marginTop: 3 }}>
                    {(r.result?.counts?.total ?? 0)} tickets · {(r.result?.counts?.late ?? 0)} late ·{' '}
                    {r.updated_at ? new Date(r.updated_at).toLocaleString() : ''}</div>
                </button>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JiraWorkspace;
